import { RefObject, useEffect } from 'react';

/**
 * overflow-x 스크롤 컨테이너에 "마우스 드래그로 스크롤"을 부여한다.
 *
 * 배경: 손수 만든 스냅 캐러셀(overflow-x-auto + scroll-snap + 스크롤바 숨김)은
 * 터치 스와이프·트랙패드 가로 스크롤은 되지만 마우스로는 조작 수단이 없다.
 * md 브레이크포인트가 1280px 로 오르면서 태블릿 밴드(800~1279px)의 데스크톱
 * 브라우저(마우스) 사용자가 모바일 캐러셀을 만나게 되어 필요해졌다.
 *
 * - 마우스 포인터만 처리(터치는 네이티브 스와이프 그대로).
 * - 드래그 중에는 scroll-snap 을 끄고(끊김 방지), 놓으면 가장 가까운 스냅
 *   자식의 중앙으로 부드럽게 정착시킨 뒤 snap 을 복원한다.
 * - 8px 이상 드래그했으면 직후 click 1회를 캡처 단계에서 무시해
 *   카드 클릭(모달 열림)이 오발동하지 않게 한다.
 */
export function useDragScroll(ref: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const DRAG_THRESHOLD_PX = 8;
    const SNAP_RESTORE_DELAY_MS = 400;

    let dragging = false;
    let moved = false;
    let startX = 0;
    let startScrollLeft = 0;
    let snapRestoreTimer: ReturnType<typeof setTimeout> | undefined;

    const restoreSnapLater = () => {
      clearTimeout(snapRestoreTimer);
      snapRestoreTimer = setTimeout(() => {
        el.style.scrollSnapType = '';
      }, SNAP_RESTORE_DELAY_MS);
    };

    const settleToNearestChild = () => {
      const viewportCenter = el.scrollLeft + el.clientWidth / 2;
      let nearest: { left: number; dist: number } | undefined;
      for (const child of Array.from(el.children) as HTMLElement[]) {
        const childCenter = child.offsetLeft + child.offsetWidth / 2;
        const dist = Math.abs(childCenter - viewportCenter);
        if (!nearest || dist < nearest.dist) {
          nearest = { left: childCenter - el.clientWidth / 2, dist };
        }
      }
      if (nearest) el.scrollTo({ left: nearest.left, behavior: 'smooth' });
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      dragging = true;
      moved = false;
      startX = e.clientX;
      startScrollLeft = el.scrollLeft;
      clearTimeout(snapRestoreTimer);
      // 텍스트 선택·이미지 네이티브 드래그 시작 방지 (click 은 그대로 발생)
      e.preventDefault();
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!dragging) return;
      const dx = e.clientX - startX;
      if (!moved) {
        if (Math.abs(dx) < DRAG_THRESHOLD_PX) return;
        moved = true;
        // 드래그 중 snap 이 켜져 있으면 scrollLeft 대입과 스냅 보정이 싸워 덜컥거림
        el.style.scrollSnapType = 'none';
        el.setPointerCapture(e.pointerId);
      }
      el.scrollLeft = startScrollLeft - dx;
    };

    const onPointerEnd = (e: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      if (el.hasPointerCapture(e.pointerId)) {
        el.releasePointerCapture(e.pointerId);
      }
      if (!moved) return;

      // 드래그 직후의 click 은 카드 클릭이 아니라 드래그 종료 — 1회만 삼킨다
      const suppressClick = (clickEvent: MouseEvent) => {
        clickEvent.stopPropagation();
        clickEvent.preventDefault();
      };
      el.addEventListener('click', suppressClick, { capture: true, once: true });
      // 드래그로 click 이 아예 발생하지 않는 경우(포인터가 밖에서 릴리즈) 리스너 잔류 방지
      setTimeout(() => el.removeEventListener('click', suppressClick, { capture: true }), 0);

      settleToNearestChild();
      restoreSnapLater();
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerEnd);
    el.addEventListener('pointercancel', onPointerEnd);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerEnd);
      el.removeEventListener('pointercancel', onPointerEnd);
      clearTimeout(snapRestoreTimer);
      el.style.scrollSnapType = '';
    };
  }, [ref]);
}
